'use client';

import React, { useState } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface EmailInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'type'
> {
  label?: React.ReactNode;
  error?: string;
  helperText?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  validateOnBlur?: boolean; // Validate when user leaves the field (default: true)
  validateOnChange?: boolean; // Validate as user types (default: false)
  forceLightMode?: boolean; // Force light mode styling (for public pages)
}

// Simple email validation regex (matches most valid emails)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailInput({
  label,
  error: externalError,
  helperText,
  id,
  required,
  disabled,
  className = '',
  value,
  onChange,
  onFocus,
  onBlur,
  validateOnBlur = true,
  validateOnChange = false,
  forceLightMode = false,
  ...props
}: EmailInputProps) {
  const [internalError, setInternalError] = useState<string>('');
  const [touched, setTouched] = useState(false);

  // Use dark mode context if available, otherwise default to light mode
  let contextDarkMode = false;
  try {
    const context = useDarkMode();
    contextDarkMode = context.darkMode;
  } catch {
    // Context not available (e.g., public pages), use light mode
    contextDarkMode = false;
  }

  // Apply forceLightMode override if needed
  const darkMode = forceLightMode ? false : contextDarkMode;

  // Generate a unique ID if not provided (using React.useId for SSR compatibility)
  const generatedId = React.useId();
  const inputId = id || generatedId;

  // Validate email
  const validateEmail = (email: string): string => {
    if (!email || email.trim() === '') {
      // Empty is only invalid if required
      return required ? 'Email address is required' : '';
    }

    if (!EMAIL_REGEX.test(email)) {
      return 'Invalid email address';
    }

    return '';
  };

  // Handle blur event
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);

    if (validateOnBlur) {
      const errorMsg = validateEmail(e.target.value);
      setInternalError(errorMsg);
    }

    onBlur?.(e);
  };

  // Handle change event
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Always clear error when user starts typing
    if (touched) {
      setInternalError('');
    }

    // Validate on change if enabled
    if (validateOnChange && touched) {
      const errorMsg = validateEmail(e.target.value);
      setInternalError(errorMsg);
    }

    onChange?.(e);
  };

  // Clear internal error when external error appears (parent component validation)
  // (adjust-state-during-render pattern, https://react.dev/learn/you-might-not-need-an-effect)
  const [prevExternalError, setPrevExternalError] = useState(externalError);
  if (prevExternalError !== externalError) {
    setPrevExternalError(externalError);
    if (externalError) {
      setInternalError('');
    }
  }

  // Determine which error to show (external takes precedence)
  const displayError = externalError || internalError;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={inputId}
          className={`mb-2 block text-[13px] font-medium ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <input
        id={inputId}
        type="email"
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        required={required}
        disabled={disabled}
        className={`block w-full rounded-md px-3 py-1.5 text-base outline-1 -outline-offset-1 transition-all sm:text-sm/6 ${
          darkMode
            ? 'bg-white/5 text-gray-100 outline-white/10 placeholder:text-gray-500'
            : 'bg-white text-gray-900 outline-gray-300 placeholder:text-gray-400'
        } ${
          darkMode
            ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
            : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
        } ${
          displayError
            ? darkMode
              ? 'outline-red-500/50 focus:outline-red-500'
              : 'outline-red-500 focus:outline-red-500'
            : ''
        } ${disabled ? 'disabled:cursor-not-allowed disabled:opacity-60' : ''}`}
        {...props}
      />

      {(displayError || helperText) && (
        <p
          className={`mt-2 text-sm ${
            displayError ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {displayError || helperText}
        </p>
      )}
    </div>
  );
}
